import React from 'react';
import { State, OnActionArgs } from '..';
import { INotificationContext } from 'utils/NotificationManager';
import { KeysOfType } from 'component_utils/typescriptUtils';
import Translation from 'utils/Language/Translation';
import { isNil } from 'lodash';
import { IConfigDto } from 'interfaces';

type Find<S, P, R, T> = (state: OnActionArgs<S, P, R>) => Promise<T>;

interface EventProps {
  notifications: INotificationContext;
  config: IConfigDto;
}

interface Config<S, P, A, R, T> {
  nextState: number;
  repeatState: number;
  targetKey: KeysOfType<S, Array<T>>;
  targetQty?: KeysOfType<S, number>;
  find: Find<S, P, R, T>;
  attributes?: Partial<A>;
  sfx?: any;
  allowProgressThroughOKScan?: boolean;
  cycleId?: string;
}

function getMetaKeys<S>(targetKey: any) {
  return {
    shouldRepeat: `state-find-any-${targetKey}-should-repeat`,
  };
}

const StateFindAnyList = function <S, P extends EventProps, A extends { hint: string }, R, T>({
  nextState,
  repeatState,
  targetKey,
  targetQty,
  allowProgressThroughOKScan,
  find,
  attributes = {},
  sfx,
  cycleId
}: Config<S, P, A, R, T>): State<S, P, A, R> {
  const { shouldRepeat } = getMetaKeys<S>(cycleId);
  return {
    onEnter: async (args) => {
      if (targetQty) {
        const currentList = args.state[targetKey] as unknown as Array<T>;
        const qty = args.state[targetQty] as unknown as number;
        const remaining = qty - currentList.length;
        if (remaining <= 0) {
          return {
            type: 'MOVE',
            nextState,
            data: {
              [shouldRepeat]: undefined
            } as any
          };
        } else if (sfx) {
          const audio = new Audio(sfx);
          audio.play();
        }
      } else {
        if (sfx) {
          const audio = new Audio(sfx);
          audio.play();
        }
      }
    },
    onAction: async (args) => {
      if ((!targetQty || allowProgressThroughOKScan) && (args.inputs[0]?.match(args.props.config.settings.scannerOk) || args.inputs[0] === '')) {
        return {
          type: 'MOVE',
          data: {
            [shouldRepeat]: undefined
          },
          nextState: nextState,
        };
      }

      // process the found scan
      const found = await find(args);
      if (isNil(found)) {
        throw args.props.notifications.add('danger', <Translation name="T.errors.notFound" />);
      }

      const currentList = args.state[targetKey] as unknown as Array<T>;
      const newList = [...currentList, found];
      let shouldProceed = false
      if (targetQty) {
        const qty = args.state[targetQty] as unknown as number;
        const remaining = qty - newList.length;
        shouldProceed = remaining <= 0
      }
      return {
        type: 'MOVE',
        data: {
          [targetKey]: newList,
          [shouldRepeat]: shouldProceed ? undefined : true,
        } as any,
        nextState: shouldProceed ? nextState : repeatState
      };
    },
    attributes: {
      ...(attributes as any),
    },

    nodeName: 'T.states.findAnyList',
    outputs: [
      {
        name: 'onRepeat',
        to: repeatState,
      },
      {
        name: 'onDone',
        to: nextState,
      },
    ],
  };
};


interface RepeatConfig<S, P, A, R> {
  nextState: number;
  findAnyListState: number;
  cycleId: string;
}
export const StateFindAnyListRepeat = function <S, P extends EventProps, A extends {}, R>({
  cycleId, nextState, findAnyListState
}: RepeatConfig<S, P, A, R>): State<S, P, A, R> {
  const { shouldRepeat } = getMetaKeys<S>(cycleId);
  return {
    onEnter: async ({ state }) => {
      console.log(state)
      return {
        type: 'MOVE',
        nextState: (state as any)[shouldRepeat] === true ? findAnyListState : nextState,
      };
    },

    attributes: {} as any,
    nodeName: 'T.states.findAnyListRepeat',
    outputs: [
      {
        name: 'onProgress',
        to: nextState,
      },
      {
        name: 'onRepeat',
        to: findAnyListState,
      },
    ],
  };
};

export default StateFindAnyList;
